/**
 * Wait until user space state (including `workspace_filter`) has finished
 * restoring. Manual `/work/:entityIdentifier` navigation must run after this
 * so `ensureDivisionFilterForNavigation` sees the applied filter and is not
 * overwritten by a late restore.
 *
 * @param {{ restoreSpaceState: () => Promise<unknown> }} appState
 * @returns {Promise<void>}
 */
export async function waitForRestoredWorkspaceFilter(appState) {
    await appState.restoreSpaceState();
}

/**
 * Decide whether the work-entity guard should skip EntityNavigator.
 *
 * `skipEntityNavigation` is set for URL-only sync when the entity is already
 * active (`RouteHelper.updateUrlForEntity` / `stopEntityNavigation`). Honor it
 * even if `entity_navigate_with_locator` is still true — that flag is held for
 * ~550ms after already-on-URL locate flows and must not defeat a later URL sync.
 *
 * Locator navigations must call `armLocatorRouterPush` immediately before
 * `router.push` (after any await such as `getEntityUrlFromId`), so they are
 * never skipped.
 *
 * @param {{ skipEntityNavigation?: boolean }} flags
 * @returns {boolean}
 */
export function shouldSkipWorkEntityNavigation({
    skipEntityNavigation = false,
} = {}) {
    return Boolean(skipEntityNavigation);
}

/**
 * Arm locator navigation flags immediately before `router.push`.
 *
 * Must run after any await in the locator path. Clearing skip only at the
 * start of `navigateToEntityById` leaves a gap where `stopEntityNavigation`
 * or `updateUrlForEntity` can set skip again and make the guard skip
 * EntityNavigator.
 *
 * @param {{ skipEntityNavigation: boolean }} routeHelper
 * @param {{ entity_navigate_with_locator: boolean }} appState
 * @returns {void}
 */
export function armLocatorRouterPush(routeHelper, appState) {
    routeHelper.skipEntityNavigation = false;
    appState.entity_navigate_with_locator = true;
}

/**
 * Build the in-app work entity path for a universal id (HANDLE-SEQUENCE).
 *
 * @param {string|null|undefined} universalId
 * @returns {string|null}
 */
export function getWorkEntityPathFromUniversalId(universalId) {
    if (!universalId) {
        return null;
    }

    return `/work/${universalId}`;
}

/**
 * Chat/shared `/work/...` links must locate even when the router is already on
 * that path — otherwise the work-entity guard skips EntityNavigator and any
 * active Work Filters keep hiding the item.
 *
 * @param {string|null|undefined} currentPath
 * @param {string|null|undefined} universalId
 * @returns {boolean}
 */
export function isAlreadyOnWorkEntityPath(currentPath, universalId) {
    const url = getWorkEntityPathFromUniversalId(universalId);

    return Boolean(url && currentPath === url);
}
